'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from 'react';
import { ContestConfig } from '@/lib/types/ContestConfig';
import { BrandTheme } from '@/lib/theme/brand-config';
import { Translations } from '@/smarttext/types';
import { clientAppLogError } from '@/lib/logging/clientAppLog';

interface ContestData {
  id: number;
  contestCode: string;
  contestName?: string;
  brandName?: string;
  contestLanguage?: string;
  contestType: string;
  description?: string;
  country?: string;
  landingURL?: string;
  isActive: boolean;
}

interface ContestContextType {
  contest: ContestData | null;
  config: ContestConfig | null;
  loading: boolean;
  error: string | null;
  theme: BrandTheme | null;
  translations: Translations[string] | null;
  translationLanguage: string | null;
  isInMaintenance: boolean;
  refresh: () => Promise<void>;
}

export const ContestContext = createContext<ContestContextType | undefined>(undefined);

export function useContest() {
  const context = useContext(ContestContext);
  if (context === undefined) {
    throw new Error('useContest must be used within a ContestProvider');
  }
  return context;
}

interface ContestProviderProps {
  children: ReactNode;
  contestCode: string;
  /** Optional initial contest data from server-side fetch - prevents duplicate API calls */
  initialContest?: ContestData | null;
  /** Optional initial config from server-side fetch - prevents duplicate API calls */
  initialConfig?: ContestConfig | null;
  /** Optional initial theme from server-side fetch - prevents duplicate API calls */
  initialTheme?: BrandTheme | null;
  /** Optional initial translations from server-side fetch - prevents duplicate API calls */
  initialTranslations?: Translations[string] | null;
  /** Optional initial maintenance mode state from server-side check */
  initialIsInMaintenance?: boolean;
}

export function ContestProvider({
  children,
  contestCode,
  initialContest,
  initialConfig,
  initialTheme,
  initialTranslations,
  initialIsInMaintenance
}: ContestProviderProps) {
  const [contest, setContest] = useState<ContestData | null>(initialContest || null);
  const [config, setConfig] = useState<ContestConfig | null>(initialConfig || null);
  const [loading, setLoading] = useState<boolean>(!initialContest); // Only loading if no initial data
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<BrandTheme | null>(initialTheme || null);
  const [translations, setTranslations] = useState<Translations[string] | null>(initialTranslations || null);
  const [translationLanguage, setTranslationLanguage] = useState<string | null>(
    initialContest?.contestLanguage || null
  );
  // Maintenance state is set from SSR and doesn't change during client-side navigation
  const [isInMaintenance] = useState<boolean>(initialIsInMaintenance || false);

  const loadContestData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch contest data and config from API (reads from CNT_CONTESTS and CNT_CONTEST_CONFIG)
      const response = await fetch(`/api/contests/${encodeURIComponent(contestCode)}`, {
        method: 'GET',
      });

      if (!response.ok) {
        // If not found by code, try landing URL resolution
        if (typeof window !== 'undefined') {
          const currentURL = window.location.pathname;
          const urlResponse = await fetch(
            `/api/contests/by-url?url=${encodeURIComponent(currentURL)}`,
            { method: 'GET' }
          );

          if (!urlResponse.ok) {
            throw new Error(`Contest not found: ${contestCode}`);
          }

          const urlResult = await urlResponse.json();
          
          if (urlResult.data) {
            setContest(urlResult.data.contest);
            setConfig(urlResult.data.config);
            setTheme(urlResult.data.theme);
            // Translations come from SSR, not from API
            setTranslations(null);
            setTranslationLanguage(urlResult.data.contest?.contestLanguage || null);
          } else {
            throw new Error(`Contest not found: ${contestCode}`);
          }
        } else {
          throw new Error(`Contest not found: ${contestCode}`);
        }
      } else {
        const result = await response.json();
        
        if (result.data) {
          setContest(result.data.contest);
          setConfig(result.data.config);
          setTheme(result.data.theme);
          // Translations come from SSR, not from API
          setTranslations(null);
          setTranslationLanguage(result.data.contest?.contestLanguage || null);
        } else {
          throw new Error('Invalid response format');
        }
      }

    } catch (err) {
      clientAppLogError({
        level: 'ERROR',
        codeSourceName: 'ContestContext',
        error: err instanceof Error ? err : new Error(String(err)),
      });
      setError(err instanceof Error ? err.message : 'Failed to load contest');
      setContest(null);
      setConfig(null);
      setTheme(null);
      setTranslations(null);
      setTranslationLanguage(null);
    } finally {
      setLoading(false);
    }
  }, [contestCode]);

  // Memoize refresh function so it doesn't change on every render
  const refresh = useCallback(async () => {
    await loadContestData();
  }, [loadContestData]);

  // Only fetch if we don't have initial data
  useEffect(() => {
    if (contestCode && !initialContest) {
      loadContestData();
    }
  }, [contestCode, initialContest, loadContestData]);

  // NOTE: Translations MUST be provided via initialTranslations from SSR.
  // They are NOT fetched on the client side to ensure:
  // 1. Server-side rendering delivers full HTML with translations
  // 2. No flash of untranslated content in the browser
  // 3. Reduced client-side API calls and better performance

  const value = useMemo(() => ({
    contest,
    config,
    loading,
    error,
    theme,
    translations,
    translationLanguage,
    isInMaintenance,
    refresh,
  }), [
    contest,
    config,
    loading,
    error,
    theme,
    translations,
    translationLanguage,
    isInMaintenance,
    refresh
  ]);

  return (
    <ContestContext.Provider value={value}>
      {children}
    </ContestContext.Provider>
  );
}

/**
 * Use Contest Config Hook
 * Convenience hook to access only the config
 *
 * @returns Contest configuration
 */
export function useContestConfig(): ContestConfig | null {
  const { config } = useContest();
  return config;
}

/**
 * Use Translations Hook
 * Convenience hook to access translations from the contest context
 *
 * @returns Translation context for use with translation hooks
 */
export function useContestTranslations() {
  const { translations, translationLanguage, error } = useContest();

  return useMemo(() => ({
    translations,
    language: translationLanguage,
    error,
  }), [translations, translationLanguage, error]);
}

/**
 * Use Translation Language Hook
 * Convenience hook to access the current contest language
 *
 * @returns Current contest language code
 */
export function useContestLanguage(): string | null {
  const { translationLanguage } = useContest();
  return translationLanguage;
}

